import {DatePicker, Form, Input, InputNumber, Select, Spin, Switch} from 'antd';
import {groupThousands, parseThousands} from '../../v2/numberFormat';
import styles from '../overview.module.scss';
import {mutualFundsTypes} from 'pages/AssetManagement/modules/constants';
import {useMutation, useQuery} from '@apollo/client';
import {GET_ALL_COUNTRIES} from 'graphql/queries/users';
import {getUserTimezone} from 'utils/helper';
import moment from 'moment';
import {InfoCircleOutlined} from '@ant-design/icons';
import v2 from '../../v2/instrumentV2.module.scss';
import {FormCard} from '../../v2/FormCard';
import {useRemainingFields} from 'pages/AssetManagement/v2/useRemainingFields';
import {InstrumentActions} from '../../v2/InstrumentActions';
import {FUND_OVERVIEW_MOCK} from '../../v2/devPrefill';
import {useDispatch, useSelector} from 'react-redux';
import {handleMutualFunds} from 'redux/store/assetClass';
import {useEffect, useState} from 'react';
import {createMutualFundsStepTwoRoute} from 'pages/AssetManagement/Bonds/routes';
import {useNavigate, useParams} from 'react-router-dom';
import {GET_MUTUAL_FUND_BY_LANGUAGE} from 'graphql/queries/assetManagement';
import {toast} from 'react-toastify';
import {UPDATE_MUTUAL_FUND} from 'graphql/mutations/assetManagement';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {handleUpdateMutualFund} from 'pages/AssetManagement/actions';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';

export const MutualFundsOverview = () => {
  const [form] = Form.useForm();
  const {assetId, lang, id} = useParams();

  // The action bar defaults `remaining` to 0, so a screen that never
  // passes it reports "All required fields on this screen are complete"
  // on a blank form. These are the names carrying `required: true` below.
  const REQUIRED_FIELDS = [
    'ticker',
    'type',
    'currentNetAssetValue',
    'currentNavDate',
    'inceptionDate',
    'netAssetValueAtInception',
  ];
  const [remaining, countRemaining] = useRemainingFields(
    form,
    REQUIRED_FIELDS,
    id,
  );
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [updatedValues, setUpdatedValues] = useState({});

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fieldValues = useSelector(state => state?.assetClass?.mutualFunds);
  const {data: getAllCountries, loading} = useQuery(GET_ALL_COUNTRIES, {
    errorPolicy: 'all',
  });

  const pageOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.assetClasses),
  );

  const {
    data,
    refetch,
    loading: loadingInstrument,
  } = useQuery(GET_MUTUAL_FUND_BY_LANGUAGE, {
    variables: {
      getFinancialInstrumentId: id,
      language: lang,
    },
    skip: !id,
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  const [updateMutualFund, {loading: loadingUpdate}] = useMutation(
    UPDATE_MUTUAL_FUND,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  // Only the fields this screen owns — a blanket spread leaks the whole record
  // into the form store and then into the mutation.
  const applyValues = source => {
    if (!source) return;
    form.setFieldsValue({
      ticker: source.ticker,
      isin: source.isin,
      type: source.type,
      // `countries` comes back as [Topic]; the Select and mutation use ids.
      countries: Array.isArray(source.countries)
        ? source.countries.map(country =>
            typeof country === 'string' ? country : country?.id,
          )
        : source.countries,
      allowsAutoInvest: source.allowsAutoInvest,
      currentNetAssetValue: source.currentNetAssetValue,
      netAssetValueAtInception: source.netAssetValueAtInception,
      currentNavDate: source.currentNavDate
        ? moment(source.currentNavDate, 'YYYY-MM-DD HH:mm:ss')
        : null,
      inceptionDate: source.inceptionDate
        ? moment(source.inceptionDate, 'YYYY-MM-DD HH:mm:ss')
        : null,
    });
  };

  useEffect(() => {
    applyValues(data?.getFinancialInstrument?.data);
  }, [data?.getFinancialInstrument?.data]);

  useEffect(() => {
    if (fieldValues?.ticker && !id) applyValues(fieldValues);
  }, [fieldValues]);

  if (loadingInstrument) {
    return (
      <div className="query-loading">
        <Spin />
        <h1>Loading...</h1>
      </div>
    );
  }

  return (
    <div>
      <div className={v2.bodyHead}>
        <h2>General information</h2>
        <p>
          Identifier, classification and the net asset value this fund prices
          from.
        </p>
      </div>

      <Form
        form={form}
        onValuesChange={countRemaining}
        onFinish={values => {
          // ticker / isin / countries belong to the base input; the rest to
          // MutualFundInput. Previously currentNavDate and currentNetAssetValue
          // were destructured out of the update and never sent.
          const payload = {
            type: values.type,
            currentNetAssetValue: values.currentNetAssetValue,
            netAssetValueAtInception: values.netAssetValueAtInception,
            currentNavDate: values.currentNavDate,
            inceptionDate: values.inceptionDate,
            allowsAutoInvest: values.allowsAutoInvest,
            baseFinancialInstrumentInput: {
              ticker: values.ticker,
              isin: values.isin,
              countries: values.countries,
            },
          };
          if (id) {
            setOpenUpdateModal(true);
            setUpdatedValues(payload);
          } else {
            // Keep the shared draft slice flat — two screens nesting under
            // `baseFinancialInstrumentInput` would clobber each other on merge.
            // eslint-disable-next-line no-unused-vars
            const {baseFinancialInstrumentInput, ...flat} = payload;
            dispatch(
              handleMutualFunds({
                ...flat,
                ticker: values.ticker,
                isin: values.isin,
                countries: values.countries,
              }),
            );
            navigate(
              createMutualFundsStepTwoRoute({param: assetId, name: 'funds'}),
            );
          }
        }}
        layout="vertical">
        <FormCard
          title="Basic information"
          description="How this fund is identified and classified">
          <div className={styles.form}>
            <Form.Item
              rules={[
                {required: true, message: 'Please provide a ticker'},
                {
                  max: 6,
                  message: 'Ticker Length cannot be more than 6 Characters',
                },
              ]}
              className={styles.smallWidth}
              name="ticker"
              label="Ticker">
              <Input placeholder="BRCVM" />
            </Form.Item>
            <Form.Item
              className={styles.smallWidth}
              name="isin"
              label="ISIN (International Securities Identification Number)">
              <Input />
            </Form.Item>
            <Form.Item
              rules={[{required: true, message: 'Please provide a type'}]}
              className={styles.smallWidth}
              name="type"
              label="Type">
              <Select>
                {mutualFundsTypes.map(type => (
                  <Select.Option key={type.value}>{type.name}</Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="allowsAutoInvest"
              label="Allows Auto Invest"
              valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item
              name="countries"
              className={styles.largeWidth}
              label="Countries">
              <Select
                loading={loading}
                allowClear
                disabled={loading}
                mode="multiple"
                showSearch
                optionFilterProp="search"
                filterOption>
                {getAllCountries?.getAllCountries?.map(country => (
                  <Select.Option search={country?.name} key={country?.id}>
                    <div className={styles['all-data']}>
                      <img src={country?.icon} alt="" />
                      <span>{country?.name}</span>
                    </div>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        </FormCard>

        <FormCard
          title="NAV metrics"
          description="Net asset value and the dates it is measured against">
          <div className={styles.infoHeader}>
            <InfoCircleOutlined />
            <p>
              <span>NAV:</span> Net Asset Value
            </p>
          </div>
          <Form.Item
            className={v2.half}
            rules={[{required: true, message: 'This is a Required Field'}]}
            name="currentNetAssetValue"
            label="Current NAV">
            <InputNumber
              className={styles['input-number']}
              style={{width: '100%'}}
              placeholder="10,000"
              disabled={id}
              min={0}
              formatter={groupThousands}
              parser={parseThousands}
            />
          </Form.Item>
          <Form.Item
            className={v2.half}
            name="currentNavDate"
            rules={[{required: true, message: 'This is  a Required Field'}]}
            label="Current NAV Date">
            <DatePicker
              style={{width: '100%'}}
              disabled={id}
              renderExtraFooter={() => (
                <div style={{textAlign: 'center'}}>
                  <p>Your timezone is: {getUserTimezone()}</p>
                </div>
              )}
              showTime={{
                defaultValue: moment('00:00', 'HH:mm:ss'),
              }}
              format="DD MMM YYYY, HH:mm"
            />
          </Form.Item>
          <Form.Item
            className={v2.half}
            rules={[{required: true, message: 'This is a Required Field'}]}
            name="inceptionDate"
            label="Inception Date">
            <DatePicker
              style={{width: '100%'}}
              renderExtraFooter={() => (
                <div style={{textAlign: 'center'}}>
                  <p>Your timezone is: {getUserTimezone()}</p>
                </div>
              )}
              showTime={{
                defaultValue: moment('00:00', 'HH:mm:ss'),
              }}
              format="DD MMM YYYY, HH:mm"
            />
          </Form.Item>
          <Form.Item
            className={v2.half}
            rules={[{required: true, message: 'This is a Required Field'}]}
            name="netAssetValueAtInception"
            label="NAV at Inception">
            <InputNumber
              className={styles['input-number']}
              style={{width: '100%'}}
              placeholder="10,000"
              min={0}
              formatter={groupThousands}
              parser={parseThousands}
            />
          </Form.Item>
        </FormCard>

        <InstrumentActions
          remaining={remaining}
          onPrepopulate={() => {
            form.setFieldsValue(FUND_OVERVIEW_MOCK);
            countRemaining();
          }}
          disabled={!pageOperations?.update}
          label="Save and proceed"
        />
      </Form>
      <ConfirmModal
        openModal={openUpdateModal}
        setOpenModal={() => setOpenUpdateModal(false)}
        loading={loadingUpdate}
        confirmText={'Are you sure you want to Update this Fields'}
        title="Update Mutual Fund"
        handleOperation={() =>
          handleUpdateMutualFund(
            updateMutualFund,
            id,
            lang,
            updatedValues,
            refetch,
            setOpenUpdateModal,
            true,
          )
        }
      />
    </div>
  );
};
